import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { Section, SectionHeading, Reveal } from "@/components/primitives";
import { api, getContent, formatApiErrorDetail, trackEvent } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Star, Spinner } from "@phosphor-icons/react";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [loadingKey, setLoadingKey] = useState(null);
  const navigate = useNavigate();
  const { isAuthed } = useAuth();

  useEffect(() => {
    trackEvent("page_view", { page: "pricing" });
    trackEvent("license_page_visit");
    getContent("plans").then(setPlans).catch(() => {});
  }, []);

  const choose = async (plan) => {
    trackEvent("cta_click", { cta: "select_plan", plan: plan.name });
    if (plan.cta === "Contact Sales" || !plan.lookup_key) {
      navigate("/contact");
      return;
    }
    if (!isAuthed) {
      toast.info("Create an account to purchase a license.");
      navigate("/register");
      return;
    }
    setLoadingKey(plan.lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.lookup_key,
        origin_url: window.location.origin,
      });
      trackEvent("checkout_started", { plan: plan.name });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout.");
      setLoadingKey(null);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Membership / License"
        title="Choose your Q-Core license."
        sub="License fees provide access to software and services. Plans are modular and can be updated by the company at any time."
      />

      <Section grid>
        <div className="grid md:grid-cols-3 gap-6 items-stretch" data-testid="pricing-grid">
          {plans.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.08}>
              <div
                data-testid={`plan-${i}`}
                className={`relative h-full rounded-2xl border p-7 flex flex-col ${
                  p.highlight ? "border-[#2DD4BF]/50 bg-[#2DD4BF]/[0.04]" : "border-white/10 bg-[#0A0B10]"
                }`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 left-7 inline-flex items-center gap-1.5 rounded-full bg-[#2DD4BF] px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-[#030305]">
                    <Star size={11} weight="fill" /> Most Popular
                  </span>
                )}
                <h3 className="text-lg font-bold text-white">{p.name}</h3>
                <p className="text-sm text-[#94A3B8] mt-1">{p.tagline}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="text-4xl font-black text-white">{p.price}</span>
                  <span className="text-sm text-[#94A3B8]">{p.period}</span>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-[#cbd5e1]">
                      <Check size={16} weight="bold" className="text-[#2DD4BF] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => choose(p)}
                  disabled={loadingKey === p.lookup_key}
                  data-testid={`plan-cta-${i}`}
                  className={`mt-7 w-full px-6 py-3.5 rounded-full font-semibold transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-70 ${
                    p.highlight ? "bg-[#2DD4BF] text-[#030305] hover:bg-white" : "border border-white/15 text-white hover:bg-white/5"
                  }`}
                >
                  {loadingKey === p.lookup_key ? (
                    <><Spinner size={16} className="animate-spin" /> Starting…</>
                  ) : (
                    p.cta
                  )}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 rounded-xl border border-amber-400/25 bg-amber-400/[0.04] p-6">
          <p className="text-sm text-amber-200/90 leading-relaxed">
            Trading activity involves risk. License fees provide access to software and services and do not represent
            an investment or guaranteed return. <span className="font-mono text-xs">[INSERT FINAL LICENSE TERMS]</span>
          </p>
        </div>
      </Section>
    </div>
  );
}
